import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { FiCheck } from 'react-icons/fi';

import styles from './styles.scss';

export default function ListInput({
    options,
    groups,
    value,
    toggle,
    multiple,
    grid,
    containerClassName,
    choiceClassName,
    activeClassName,
    groupLabelClassName,
    fullWidthClassName,
    renderOption,
    style,
    onChange,
}) {
    const isSelected = (selected) => {
        return multiple ? value.includes(selected) : value === selected;
    };
    const handleClick = (selected, e) => {
        e.preventDefault();
        e.stopPropagation();

        const add = toggle && isSelected(selected);

        if (multiple) {
            if (add) {
                onChange(value.filter((item) => item !== selected));
            } else {
                onChange([...new Set([...value, selected])]);
            }
        } else {
            if (add) {
                onChange(undefined);
            } else {
                onChange(selected);
            }
        }
    };

    const grouped = useMemo(() => {
        const result = new Map((groups || []).map((group) => [group.id, { ...group, options: [] }]));

        if (!result.get('default')) {
            result.set('default', { id: 'default', options: [], label: '' });
        }

        if (groups) {
            for (const option of options) {
                if (option.group) {
                    const group = result.get(option.group);

                    if (group) {
                        group.options.push(option);
                    } else {
                        result.set(option.group, { id: 'default', options: [option], label: option.group });
                    }
                }
            }

            for (const group of [...result.values()]) {
                if (!group.options.length) {
                    result.delete(group.id);
                }
            }
        }

        return new Map(
            [...result.entries()].sort(([, a], [, b]) => {
                const indexA = Number.isInteger(a.index) ? a.index : a.id;
                const indexB = Number.isInteger(b.index) ? b.index : b.id;

                return indexB - indexA;
            })
        );
    }, [groups]);

    const renderOptions = (options) =>
        options.map((option) => {
            const active = isSelected(option.value);
            const Icon = option.icon;
            const content = (renderOption && renderOption({ option, active })) ||
                (option.props && option.props.children) || (
                <>
                    {Icon && (
                        <div className={styles.icon}>
                            <Icon size={24} />
                        </div>
                    )}
                    <div className={styles.label}>{(option.props && option.props.children) || option.label}</div>
                    <div className={cn({ [styles.checkMark]: true, [styles.active]: active })}>
                        <FiCheck size={24} />
                    </div>
                </>
            );

            return (
                <div
                    key={option.value || option.label}
                    className={cn(
                        styles.choice,
                        active && styles.active,
                        option.rounded && styles.rounded,
                        choiceClassName,
                        active && activeClassName,
                        option.fullWidth && fullWidthClassName
                    )}
                    onClick={onChange && handleClick.bind(null, option.value)}
                    {...option.props}
                >
                    {content}
                </div>
            );
        });

    if (grouped.size > 1) {
        return (
            <div style={style}>
                {[...grouped.values()].map(({ id, label, options }) => (
                    <div key={id}>
                        <div className={cn(styles.groupLabel, groupLabelClassName)}>{label}</div>

                        <div className={cn(styles.root, grid && styles.grid, containerClassName)}>
                            {renderOptions(options)}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className={cn(styles.root, grid && styles.grid, containerClassName)} style={style}>
            {renderOptions(options)}
        </div>
    );
}

const OptionValuePropType = PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]);

export const OptionProps = {
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    group: PropTypes.string,
    value: OptionValuePropType,
    icon: PropTypes.any,
    fullWidth: PropTypes.bool,
};
export const OptionGroupProps = {
    label: PropTypes.any,
    id: PropTypes.string,
    index: PropTypes.number,
    values: PropTypes.arrayOf(OptionValuePropType).isRequired,
    icon: PropTypes.any,
};

ListInput.propTypes = {
    value: PropTypes.oneOfType([OptionValuePropType, PropTypes.arrayOf(OptionValuePropType)]),
    options: PropTypes.arrayOf(PropTypes.shape(OptionProps)).isRequired,
    groups: PropTypes.arrayOf(PropTypes.shape(OptionGroupProps)),
    toggle: PropTypes.bool,
    grid: PropTypes.bool,
    multiple: PropTypes.bool,
    renderOption: PropTypes.func,
    style: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    containerClassName: PropTypes.string,
    choiceClassName: PropTypes.string,
    activeClassName: PropTypes.string,
    groupLabelClassName: PropTypes.string,
    fullWidthClassName: PropTypes.string,
};

ListInput.defaultProps = {
    value: null,
    groups: null,
    toggle: false,
    multiple: false,
    grid: false,
    onChoiceRef: null,
    renderOption: null,
    containerClassName: '',
    choiceClassName: '',
    activeClassName: '',
    groupLabelClassName: '',
    fullWidthClassName: '',
};

ListInput.styles = styles;
